import { Box } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Invoice from '@spike/invoice-model';
import clsx from 'clsx';
import { FunctionComponent } from 'react';
import InvoiceResumeTaxes from './InvoiceResumeTaxes';
import ResumeLine from './UI/ResumeLine';
import TotalResumeTotal from './UI/TotalResumeLine';
import { formatAmount } from './utils/OrderUtils';

export interface InvoiceResumeProps {
    invoice?: Invoice;
    loadingInvoice?: boolean;
    loadingTip?: boolean;
    className?: string;
    combineItemDiscounts?: boolean;
}

const useStyles = makeStyles(() =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        lineTopMargin: {
            marginTop: 11
        },
        balanceContainer: {
            display: 'flex',
            flexDirection: 'column',
            borderTop: '1px solid #D4D4D4',
            paddingTop: 15,
            marginTop: 15
        }
    })
);

export const InvoiceResume: FunctionComponent<InvoiceResumeProps> = props => {
    const classes = useStyles(props);

    const balance = Number(props.invoice?.balance ?? '0.00');
    const hasCredit = balance < 0;
    const combinedItemDiscounts = (
        Number(props.invoice?.totalItemsDiscount ?? 0) + Number(props.invoice?.configuredDiscounts?.total ?? 0)
    ).toFixed(2);
    const paidDepositTotal =
        props.invoice?.deposits.list.reduce((total, deposit) => {
            if (!deposit.paid) return total;

            return total + Number(deposit.amount);
        }, 0) ?? 0;

    return (
        <Box className={clsx(classes.container, props.className)}>
            <ResumeLine
                description="Subtotal"
                value={formatAmount(props.invoice?.grossTotal)}
                loading={props.loadingInvoice}
            />
            {props.combineItemDiscounts ? (
                (props.loadingInvoice || Number(combinedItemDiscounts) !== 0) && (
                    <ResumeLine
                        description="Discounts"
                        value={formatAmount(`-${combinedItemDiscounts}`)}
                        loading={props.loadingInvoice}
                        className={classes.lineTopMargin}
                    />
                )
            ) : (
                <>
                    <ResumeLine
                        description="Item in-line discounts"
                        value={formatAmount(`-${props.invoice?.totalItemsDiscount}`)}
                        loading={props.loadingInvoice}
                        className={classes.lineTopMargin}
                    />
                    <ResumeLine
                        description="Automatic Discounts"
                        value={formatAmount(`-${props.invoice?.configuredDiscounts.total}`)}
                        loading={props.loadingInvoice}
                    />
                </>
            )}

            {(props.invoice?.promocodeDiscounts ?? 0) != 0 && (
                <ResumeLine
                    description={`Promo Code ${props.invoice?.promocodes
                        .map(pc => pc.name.toLocaleUpperCase())
                        .join(', ')}`}
                    value={`-${formatAmount(props.invoice?.promocodeDiscounts?.toString())}`}
                    loading={props.loadingInvoice}
                    className={classes.lineTopMargin}
                />
            )}
            <InvoiceResumeTaxes
                taxes={props.invoice?.tax}
                loading={props.loadingInvoice}
                className={classes.lineTopMargin}
            />
            {Number(props.invoice?.surcharge ?? 0) > 0 && (
                <ResumeLine
                    description="Surcharge"
                    value={formatAmount(props.invoice?.surcharge)}
                    loading={props.loadingInvoice}
                    className={classes.lineTopMargin}
                />
            )}
            {Number(props.invoice?.tip?.amount ?? 0) > 0 && (
                <ResumeLine
                    description="Tip (optional)"
                    value={formatAmount(props.invoice?.tip?.amount)}
                    loading={props.loadingInvoice || props.loadingTip}
                    className={classes.lineTopMargin}
                />
            )}
            <TotalResumeTotal
                description="Total"
                value={formatAmount(props.invoice?.grandTotal)}
                loading={props.loadingInvoice || props.loadingTip}
                className={classes.lineTopMargin}
            />
            {paidDepositTotal > 0 && (
                <ResumeLine
                    description="Deposit"
                    value={formatAmount(`-${paidDepositTotal.toFixed(2)}`)}
                    loading={props.loadingInvoice}
                    className={classes.lineTopMargin}
                />
            )}
            {Number(props.invoice?.payments.totalWithoutDeposits) > 0 && (
                <ResumeLine
                    description="Payments"
                    value={formatAmount(`-${props.invoice?.payments.totalWithoutDeposits}`)}
                    loading={props.loadingInvoice || props.loadingTip}
                    className={classes.lineTopMargin}
                />
            )}
            {Number(props.invoice?.totalRefunds) > 0 && (
                <ResumeLine
                    description="Refunds"
                    value={formatAmount(`${props.invoice?.totalRefunds}`)}
                    loading={props.loadingInvoice || props.loadingTip}
                    className={classes.lineTopMargin}
                />
            )}
            <Box className={classes.balanceContainer}>
                <TotalResumeTotal
                    description="Balance Due"
                    value={hasCredit ? '0.00' : formatAmount(balance.toFixed(2)) ?? '0.00'}
                    loading={props.loadingInvoice || props.loadingTip}
                />
                {hasCredit && (
                    <ResumeLine
                        description="Credit due to client"
                        value={formatAmount(Math.abs(balance).toFixed(2))}
                        loading={props.loadingInvoice || props.loadingTip}
                    />
                )}
            </Box>
        </Box>
    );
};

export default InvoiceResume;
